import { connect, FormikValues } from 'formik'
import { get } from 'lodash'
import { useEffect, useState } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { useMutation, useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import Skeleton from 'react-skeleton-loader'
import { Label } from 'semantic-ui-react'
import styled from 'styled-components'
import { object, string } from 'yup'

import Button from '@/components/Button'
import CopyTextButton from '@/components/CopyTextButton'
import InlineErrorMessage from '@/components/InlineErrorMessage/InlineErrorMessage'
import InlineLoader from '@/components/InlineLoader'
import { FlexColumn, FormLabel, Row } from '@/components/StyledComponents'
import CodeEditor from '@/pages/SpaceAppBuilder/components/CodeEditor/CodeEditor'
import { apiConnectorCredentialQueryOptions } from '@/pages/SpaceAppBuilder/utilities/queries'
import { useAppSelector } from '@/store/hooks'
import { getTenantAPIHost } from '@/store/selectors'

import messages from './messages'

type HttpRequestTriggerProps = {
  readonly?: boolean
}

//<editor-fold desc="Styled Components">
const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;
`
const StyledFormLabel = styled(FormLabel)`
  margin-top: 10px;
  font-size: 0.9rem;
`
const RequestUrl = styled.p`
  margin: 0;
  font-size: 14px;
  max-width: 500px;
  display: inline-block;
  white-space: nowrap;
  overflow-x: hidden;
  overflow-y: hidden;
`
const Description = styled.p`
  color: #a0a0a0;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
  margin-top: 0.25rem;
`
const SensitiveWrapper = styled.div`
  border-color: #f1fae9;
  border-width: 0.2rem;
  border-style: solid;
  padding-left: 15px;
  height: 150px;
  width: 450px;
`
const StyledFlexEndColumn = styled(FlexColumn)`
  padding-top: 2px;
  display: flex;
  align-items: flex-start;
`
const StyledFlexColumn = styled(FlexColumn)`
  padding: 2px;
  align-items: flex-start;
`
const StyledWarning = styled(StyledFormLabel)`
  color: var(--wg-red);
`

const StyledButton = styled(Button)`
  padding: 0 0.5rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  margin-left: -9px;
  min-height: 20px;
`

const StyledPrimaryButton = styled(Button)`
  margin-left: 0;
`

const StyledSection = styled.div`
  margin-bottom: 20px;
`
//</editor-fold>

const getValidationSchema = (intl: IntlShape) => {
  return {
    payload: object()
      .typeError(({ originalValue }) => {
        try {
          JSON.parse(originalValue)
        } catch {
          return intl.formatMessage(messages.payloadInvalidJson)
        }
        return true
      })
      .required(intl.formatMessage(messages.pleaseDefinePayload)),
    clientId: string()
  }
}

const getInitialValues = (triggerData: any) => {
  return {
    payload: get(triggerData, 'payload', {}),
    clientId: triggerData.clientId
  }
}

const getHttpRequestTriggerHelpUrl =
  'https://help.workgrid.com/s/article/Setting-Up-an-HTTP-Request-Trigger-Response-for-Workshop'

const HttpRequestTrigger = ({ formik, readOnly = false }: HttpRequestTriggerProps & FormikValues) => {
  const intl = useIntl()

  const { spaceId, appId, featureId } = useParams<{ spaceId: string; appId: string; featureId: string }>()
  const { values, setFieldValue, errors, setFieldError } = formik
  const tenantAPIHost = useAppSelector(getTenantAPIHost)
  const requestUrl = `${tenantAPIHost}/v1/appbuilder/feature/${featureId}/trigger`
  const [clientSecret, setClientSecret] = useState('')
  const apiConnectorMutation = useMutation({})

  useEffect(() => {}, [featureId])

  const appQueryResult = useQuery(apiConnectorCredentialQueryOptions({ spaceId: spaceId, instanceId: appId }))

  const generateCredentials = async ({ isUpdate = false }: { isUpdate: boolean }) => {
    try {
      await apiConnectorMutation.mutateAsync(
        // @ts-expect-error
        [
          apiConnectorCredentialQueryOptions({ spaceId: spaceId, instanceId: appId }).queryKey[0],
          {
            method: isUpdate ? 'PUT' : 'POST',
            headers: { 'content-type': 'application/json' }
          }
        ],
        {
          onSuccess: (data: any) => {
            setClientSecret(data.clientSecret)
            // Reload the app api connector
            appQueryResult.refetch()
          }
        }
      )
    } catch {}
  }

  return (
    <>
      <StyledSection>
        <StyledFormLabel>{intl.formatMessage(messages.requestUrl)}</StyledFormLabel>
        <Description>{intl.formatMessage(messages.requestUrlDescription)}</Description>
        <StyledRow>
          <StyledFlexColumn>
            <Label size="tiny" basic>
              {intl.formatMessage(messages.requestMethod)}
            </Label>
          </StyledFlexColumn>

          <StyledFlexColumn>
            <RequestUrl> {requestUrl}</RequestUrl>
          </StyledFlexColumn>
          <StyledFlexEndColumn>
            <CopyTextButton text={requestUrl} />
          </StyledFlexEndColumn>
        </StyledRow>
      </StyledSection>
      <StyledSection>
        <StyledFormLabel>{intl.formatMessage(messages.credentials)}</StyledFormLabel>
        <Description>{intl.formatMessage(messages.credentialsDescription)}</Description>
        {apiConnectorMutation.isLoading ? (
          <Skeleton />
        ) : apiConnectorMutation.isError ? (
          <InlineErrorMessage />
        ) : (
          <InlineLoader useQueryResult={appQueryResult}>
            {data => (
              <StyledRow>
                {data.clientId ? (
                  <SensitiveWrapper>
                    {data.clientId && (
                      <>
                        <StyledRow>
                          <StyledFormLabel>{intl.formatMessage(messages.clientID)}</StyledFormLabel>
                        </StyledRow>
                        <StyledRow>
                          <Description>{data.clientId}</Description>
                        </StyledRow>
                      </>
                    )}
                    <StyledRow>
                      <StyledFlexColumn>
                        <StyledFormLabel>{intl.formatMessage(messages.clientSecret)}</StyledFormLabel>
                      </StyledFlexColumn>
                      {clientSecret && <StyledWarning>{intl.formatMessage(messages.onlyDisplayedOnce)}</StyledWarning>}
                    </StyledRow>
                    <StyledRow>
                      {clientSecret ? (
                        <>
                          <StyledFlexColumn>
                            <Description>{clientSecret}</Description>
                          </StyledFlexColumn>
                          <StyledFlexEndColumn>
                            <CopyTextButton text={clientSecret} />
                          </StyledFlexEndColumn>
                        </>
                      ) : (
                        <StyledFlexColumn>
                          <StyledButton
                            color={'transparent'}
                            onClick={() => generateCredentials({ isUpdate: true })}
                            size={'small'}
                          >
                            {intl.formatMessage(messages.resetCredentials)}
                          </StyledButton>
                        </StyledFlexColumn>
                      )}
                    </StyledRow>
                  </SensitiveWrapper>
                ) : (
                  <StyledPrimaryButton color={'primary'} onClick={() => generateCredentials({ isUpdate: false })}>
                    {intl.formatMessage(messages.generateCredentials)}
                  </StyledPrimaryButton>
                )}
              </StyledRow>
            )}
          </InlineLoader>
        )}
      </StyledSection>
      <CodeEditor
        label={intl.formatMessage(messages.definePayload)}
        readOnly={readOnly}
        setFieldValue={setFieldValue}
        setFieldError={setFieldError}
        defaultValue={
          typeof values?.triggerData?.payload === 'string'
            ? values.triggerData.payload
            : JSON.stringify(values.triggerData.payload)
        }
        errors={errors}
      />
    </>
  )
}

export default connect(HttpRequestTrigger)

export { getInitialValues, getValidationSchema, getHttpRequestTriggerHelpUrl }
